import type React from 'react';
import styled from 'styled-components';
import GenericPage from 'app/ui/components/layouts/generic-page';
import Typography from 'app/ui/components/atoms/typography';

const ContentWrapper = styled.div`
  max-width: 100%;
  line-height: 1.6;
`;

const StyledHeading = styled(Typography)<{ $isFirstHeading?: boolean }>`
  margin-top: ${({ $isFirstHeading, theme: { spacing } }) => ($isFirstHeading ? 0 : spacing(8))};
  margin-bottom: ${({ theme: { spacing } }) => spacing(4)};
  color: ${({ theme: { colors } }) => colors.white};
`;

const StyledTypography = styled(Typography)`
  margin-bottom: ${({ theme: { spacing } }) => spacing(4)};
  color: ${({ theme: { colors } }) => colors.lightGray};
`;

const StyledList = styled.ul`
  margin-bottom: ${({ theme: { spacing } }) => spacing(4)};
  padding-left: ${({ theme: { spacing } }) => spacing(6)};
  color: ${({ theme: { colors } }) => colors.lightGray};
`;

const StyledListItem = styled.li`
  margin-bottom: ${({ theme: { spacing } }) => spacing(2)};
`;

const TermsConditions: React.FC = () => {
  return (
    <GenericPage pageTitle="Terms & Conditions">
      <ContentWrapper>
        <StyledHeading variant="h2">
          HYROX PLATFORM <br />
          End User Licence Agreement
        </StyledHeading>

        <StyledTypography variant="body2">
          The Hyrox Platform (the "Platform") is an online fitness platform which manages and delivers to gyms Hyrox
          workouts and fitness content.
        </StyledTypography>

        <StyledTypography variant="body2">
          The Platform is operated for Hyrox by Fiit Limited (company number: 10713969) a company registered in England
          and Wales ("Fiit", "we" or "us") with registered offices at 14 East Bay Lane, The Press Centre, Queen
          Elizabeth Olympic Park, London, England, E15 2GW.
        </StyledTypography>

        <StyledTypography variant="body2">
          When you access and use of the Platform is governed by these terms ("Terms").
        </StyledTypography>

        <StyledTypography variant="body2">
          By using the Platform, you are also agreeing that you have read and understood our Privacy Policy. Please read
          these Terms carefully before you start to use the Platform. We recommend that you print a copy of these Terms
          for future reference. If you do not agree to these Terms, you must not use the Platform.
        </StyledTypography>

        <StyledHeading variant="h3">General</StyledHeading>
        <StyledTypography variant="body2">
          These Terms were last updated on <strong>31 October 2023</strong>.
        </StyledTypography>

        <StyledTypography variant="body2">
          Fiit reserves the right to amend or change these Terms at any time without notice. By using the Platform after
          any changes have been posted, you agree to any new terms.
        </StyledTypography>

        <StyledTypography variant="body2">
          We may suspend, restrict or terminate your account and your access to the Platform with or without notice if
          we believe that you have breached these Terms.
        </StyledTypography>

        <StyledHeading variant="h3">Account registration</StyledHeading>
        <StyledTypography variant="body2">
          In order to access the Platform, you'll need to register for an account.
        </StyledTypography>

        <StyledTypography variant="body2">
          You agree that you will take all steps necessary to protect your login details and keep them secret. You agree
          that you will not share your details or allow someone else to use your account. If you do not keep your login
          details secret, or if you share your account or details with someone else, you accept full responsibility for
          the consequences.
        </StyledTypography>

        <StyledHeading variant="h3">Platform terms of use</StyledHeading>

        <StyledHeading variant="h4">Licence</StyledHeading>
        <StyledTypography variant="body2">
          Subject to your compliance with these Terms and for so long as you are permitted by us to access the Platform,
          Fiit grants you a limited, non-transferable, non-sublicensable, non-exclusive, revocable licence to access the
          Platform for your own personal, non-commercial purposes. This licence includes the right to view any text,
          graphics, images, music, software, audio, video, works of authorship of any kind, and information or other
          materials that are posted, generated, provided or otherwise made available through the Platform.
        </StyledTypography>

        <StyledTypography variant="body2">
          Except as expressly permitted by these Terms, you will not copy, reproduce, modify, redistribute, sell,
          transfer, create derivative works from, decompile, reverse engineer, or disassemble the Platform or any
          content or materials within them, nor will you take any measures to interfere with or damage the Platform.
        </StyledTypography>

        <StyledHeading variant="h4">Intellectual property rights</StyledHeading>
        <StyledTypography variant="body2">
          You acknowledge and agree that all copyright, trademarks and all other intellectual property rights in the
          Platform and all material or content supplied as part of the Platform will remain at all times vested in us or
          our licensors, including Hyrox. You are permitted to use this material only as expressly authorised by us in
          accordance with these Terms. All rights not expressly granted by Fiit in these Terms are reserved.
        </StyledTypography>

        <StyledHeading variant="h4">Availability</StyledHeading>
        <StyledList>
          <StyledListItem>
            We cannot guarantee that you will have uninterrupted access to the Platform at all times. While we will make
            all effort to avoid it, access may be disrupted from time to time due to necessary maintenance, technical
            issues, network and system overloads or events outside of our control.
          </StyledListItem>
          <StyledListItem>
            You may be required to download and install updates notified to you to maintain access to the Platform.
            Failure to do so may result in certain features and services being unavailable or adversely affected.
          </StyledListItem>
          <StyledListItem>
            We do not guarantee that our site will be secure or free from bugs or viruses. You are responsible for
            configuring your information technology, computer programmes and platform to access our site. You should use
            your own virus protection software.
          </StyledListItem>
        </StyledList>

        <StyledHeading variant="h4">Accuracy</StyledHeading>
        <StyledTypography variant="body2">
          While try our best to ensure that information on the Platform is correct, it is possible that the Platform may
          contain errors or inaccuracies. We therefore reserve the right to correct any errors, inaccuracies or
          omissions and to change and update information at any time without prior notice.
        </StyledTypography>

        <StyledHeading variant="h4">Prohibitions</StyledHeading>
        <StyledTypography variant="body2">
          In addition to other prohibitions as set forth in these Terms, you are prohibited from using the Platform to:
        </StyledTypography>
        <StyledList>
          <StyledListItem>
            violate any applicable law or regulation, including, without limitation, laws governing antitrust,
            encryption, export/import control, intellectual property, obscenity, privacy, securities, gambling, and
            telecommunications, or for any unlawful purpose or to solicit others to perform or participate in any
            unlawful acts;
          </StyledListItem>
          <StyledListItem>
            post, send, receive, use, encourage, promote, facilitate or instruct others to use defamatory, harassing,
            libelous, obscene, abusive, deceptive, false, misleading, fraudulent, pornographic or threatening materials,
            or private information without the consent of the individual(s) involved;
          </StyledListItem>
          <StyledListItem>
            post, send or receive any content, including, without limitation, text, graphics, images, computer programs,
            links and "meta tags," that violates any copyright, patent, trademark, trade secret, or other intellectual
            property right of a third party;
          </StyledListItem>
          <StyledListItem>
            obtain or attempt to obtain unauthorised access to any server, system, network or account;
          </StyledListItem>
          <StyledListItem>
            interfere or attempt to interfere with the provision of the Platform to any user, host, or network by use of
            any program, script or otherwise;
          </StyledListItem>
          <StyledListItem>
            interfere or attempt to interfere with security-related features of the Platform, or features that limit or
            restrict (a) use of the Platform or (b) use or copying of any content accessible through use of the
            Platform;
          </StyledListItem>
          <StyledListItem>
            avoid any use limitations placed on the Platform, such as access and storage restrictions;
          </StyledListItem>
          <StyledListItem>monitor data or traffic on a system without permission;</StyledListItem>
          <StyledListItem>spam, phish, pharm, pretext, spider, crawl, or scrape;</StyledListItem>
          <StyledListItem>
            send, receive, or use malware, spyware, adware, key loggers, viruses, worms, harmful code and/or Trojan
            horses, or Internet Relay Chat "bots";
          </StyledListItem>
          <StyledListItem>
            engage in "hacking", "cracking", mail bombing, port scanning, denial of service, or other malicious or
            destructive activities;
          </StyledListItem>
          <StyledListItem>
            resell the Platform, in whole or in part, to any third party without our express prior written consent;
          </StyledListItem>
          <StyledListItem>
            attempt to scan or test the vulnerability of a system or network, or to breach security or authentication
            measures, without proper authorisation; or
          </StyledListItem>
          <StyledListItem>gather or store personal information or data of other users.</StyledListItem>
        </StyledList>

        <StyledHeading variant="h4">External links</StyledHeading>
        <StyledTypography variant="body2">
          We may provide links to other websites from time to time. These links are provided for your convenience. We do
          not endorse or take responsibility for the content of those websites, are not responsible for the availability
          of those websites and will not be liable in any way for any loss or damage which you may suffer by using those
          websites. If you decide to access linked websites you do so at your own risk.
        </StyledTypography>

        <StyledHeading variant="h3">Liabilities & Indemnities</StyledHeading>
        <StyledList>
          <StyledListItem>
            The Platform is provided on an "as is" and "as available" basis without any representation or endorsement
            made and we make no warranties of any kind, whether express or implied, in relation to the Platform,
            including but not limited to, implied warranties of non-infringement, compatibility, security, accuracy,
            conditions of completeness, or any implied warranty arising from course of dealing or usage or trade.
          </StyledListItem>
          <StyledListItem>
            To the fullest extent permitted by law, we exclude all conditions, warranties, representations or other
            terms, which may apply to the Platform, whether express or implied.
          </StyledListItem>
          <StyledListItem>
            To the fullest extent permitted by law, we exclude all liability (whether arising in contract, in negligence
            or otherwise) for loss or damage which you or any third party may incur in connection with the Platform
            (including but not limited to use of, or inability to use, the Platform or use of or reliance on any content
            displayed on the Platform), any website linked to the Platform and any materials posted on it.
          </StyledListItem>
        </StyledList>

        <StyledTypography variant="body2">
          We will not be liable, in contract, tort (including, without limitation, negligence), pre-contract or other
          representations or otherwise out of or in connection with these Terms for:
        </StyledTypography>
        <StyledList>
          <StyledListItem>
            any economic losses (including without limitation loss of revenues, profits, contracts, business or
            anticipated savings);
          </StyledListItem>
          <StyledListItem>any loss of goodwill or reputation; or</StyledListItem>
          <StyledListItem>any special or indirect losses.</StyledListItem>
        </StyledList>

        <StyledTypography variant="body2">
          Nothing in these Terms limits or excludes our liability for:
        </StyledTypography>
        <StyledList>
          <StyledListItem>death or personal injury by our negligence;</StyledListItem>
          <StyledListItem>fraudulent misrepresentation; or</StyledListItem>
          <StyledListItem>any other liability that cannot be excluded by law.</StyledListItem>
        </StyledList>

        <StyledTypography variant="body2">
          To the fullest extent permitted by law, you agree to indemnify and hold Fiit harmless from and against all
          damages, losses, and expenses of any kind (including reasonable legal fees and costs) arising out of your
          breach of these Terms or your access or use of the Platform.
        </StyledTypography>

        <StyledHeading variant="h3">Miscellaneous</StyledHeading>
        <StyledList>
          <StyledListItem>
            We collect personal information about you through your use of the Platform. All information that we collect
            about you is subject to our Privacy Policy.
          </StyledListItem>
          <StyledListItem>
            If any part of these Terms is deemed to be unenforceable, the enforceability of any other part of these
            Terms will not be affected.
          </StyledListItem>
          <StyledListItem>
            You may not assign, sub-licence or otherwise transfer your rights or obligations under these Terms to anyone
            else. You agree that we may assign or transfer any of our rights or obligations under these Terms without
            notice to you.
          </StyledListItem>
          <StyledListItem>
            If we delay exercising or fail to exercise or enforce any right available to us under these Terms, such
            delay or failure does not constitute a waiver of that right or any other rights under these Terms.
          </StyledListItem>
          <StyledListItem>
            These Terms between you and us shall be governed by and interpreted in accordance with the laws of England
            and Wales. The courts of England and Wales shall have non-exclusive jurisdiction to resolve any claims,
            disputes or disagreements relating to these Terms.
          </StyledListItem>
        </StyledList>

        <StyledHeading variant="h3">How to contact us</StyledHeading>
        <StyledTypography variant="body2">
          If you have any feedback, questions or complaints or any requests for technical support, then please e-mail us
          at: <strong>hello@fiit.tv</strong>. You can also write to us at 14 East Bay Lane, The Press Centre, Queen
          Elizabeth Olympic Park, London, England, E15 2GW.
        </StyledTypography>
      </ContentWrapper>
    </GenericPage>
  );
};

export default TermsConditions;
